import { useState, useRef } from "react";

export function SlideToConfirm({ onConfirm, label = "Slide to confirm", variant = "destructive", disabled = false }) {
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const trackRef = useRef(null);
  const startXRef = useRef(0);
  const trackWidthRef = useRef(0);

  const THUMB_WIDTH = 48;
  const CONFIRM_THRESHOLD = 0.85;

  const getTrackWidth = () => {
    if (trackRef.current) {
      return trackRef.current.offsetWidth - THUMB_WIDTH;
    }
    return 200;
  };

  const handleStart = (clientX) => {
    if (disabled || confirmed) return;
    setIsDragging(true);
    startXRef.current = clientX;
    trackWidthRef.current = getTrackWidth();
  };

  const handleMove = (clientX) => {
    if (!isDragging || confirmed) return;
    const diff = clientX - startXRef.current;
    const clamped = Math.max(0, Math.min(diff, trackWidthRef.current));
    setDragX(clamped);
  };

  const handleEnd = () => {
    if (!isDragging || confirmed) return;
    setIsDragging(false);
    const progress = dragX / trackWidthRef.current;
    if (progress >= CONFIRM_THRESHOLD) {
      setConfirmed(true);
      setDragX(trackWidthRef.current);
      onConfirm();
      // Reset after a delay
      setTimeout(() => {
        setConfirmed(false);
        setDragX(0);
      }, 1500);
    } else {
      setDragX(0);
    }
  };

  const handleMouseDown = (e) => handleStart(e.clientX);
  const handleMouseMove = (e) => handleMove(e.clientX);
  const handleMouseUp = () => handleEnd();

  const handleTouchStart = (e) => handleStart(e.touches[0].clientX);
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);
  const handleTouchEnd = () => handleEnd();

  const progress = trackWidthRef.current > 0 ? dragX / trackWidthRef.current : 0;

  const bgColor = variant === "destructive" 
    ? confirmed ? "bg-red-500/30" : "bg-red-950/30" 
    : confirmed ? "bg-emerald-500/30" : "bg-muted/30";
  
  const borderColor = variant === "destructive"
    ? "border-red-500/30"
    : "border-emerald-500/30";
  
  const thumbColor = variant === "destructive"
    ? confirmed ? "bg-red-500" : "bg-red-500/80"
    : confirmed ? "bg-emerald-500" : "bg-emerald-500/80";

  const textColor = variant === "destructive" ? "text-red-400/60" : "text-emerald-400/60";

  return (
    <div
      ref={trackRef}
      className={`relative h-10 rounded-sm border ${borderColor} ${bgColor} select-none overflow-hidden ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      onMouseMove={isDragging ? handleMouseMove : undefined}
      onMouseUp={isDragging ? handleMouseUp : undefined}
      onMouseLeave={isDragging ? handleEnd : undefined}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="slide-to-confirm"
    >
      {/* Label */}
      <div className={`absolute inset-0 flex items-center justify-center font-mono text-[10px] uppercase tracking-wider ${textColor} pointer-events-none`}>
        {confirmed ? "Confirmed" : label}
      </div>

      {/* Progress fill */}
      <div
        className={`absolute inset-y-0 left-0 ${variant === "destructive" ? "bg-red-500/10" : "bg-emerald-500/10"} transition-none`}
        style={{ width: `${dragX + THUMB_WIDTH}px` }}
      />

      {/* Thumb */}
      <div
        className={`absolute top-1 bottom-1 w-10 rounded-sm ${thumbColor} flex items-center justify-center cursor-grab active:cursor-grabbing transition-none`}
        style={{ left: `${dragX + 4}px`, transition: isDragging ? 'none' : 'left 0.3s ease' }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid="slide-thumb"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-white">
          <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
        </svg>
      </div>
    </div>
  );
}
